/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Layout wrapper for Product Engineering articles.
 * Provides series navigation, prev/next links, and shared MDXProvider.
 */

import { Alert, Box, Container, Typography, Stack, Chip, Divider, Button, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ListIcon from '@mui/icons-material/ViewList';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import TimerIcon from '@mui/icons-material/Schedule';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link } from '@tanstack/react-router';
import { MDXProvider } from '@/components/MDXProvider';
import { useMemo, useEffect } from 'react';
import { useThemeContext } from '@/contexts/ThemeContext';
import { usePageColors } from '@/contexts/PageColorContext';
import { prodengManifest, formatProdengDate } from '@/lib/prodengData';
import { analytics } from '@/lib/analytics';
import { useScrollDepth } from '@/hooks/useScrollDepth';

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface ProdEngArticleLayoutProps {
  title: string;
  date: string;
  author: string;
  articleNumber: number;
  seoDescription: string;
  linkedinUrl: string;
  readTime: string;
  heroImage?: string;
  tags?: string[];
  published?: boolean;
  children: React.ReactNode;
}

export function ProdEngArticleLayout({
  title,
  date,
  author,
  articleNumber,
  seoDescription,
  linkedinUrl,
  readTime,
  heroImage,
  tags = [],
  published = true,
  children,
}: ProdEngArticleLayoutProps) {
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  const { accent } = usePageColors();

  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  useScrollDepth(slug);

  useEffect(() => {
    analytics.articleViewed(slug, 'prodeng', title, readTime);
  }, [slug, title, readTime]);

  const { prev, next, total } = useMemo(() => {
    const articles = prodengManifest.articles;
    const idx = articles.findIndex((a: any) => a.articleNumber === articleNumber);
    return {
      prev: idx > 0 ? articles[idx - 1] : null,
      next: idx < articles.length - 1 ? articles[idx + 1] : null,
      total: articles.length,
    };
  }, [articleNumber]);

  const formattedDate = date ? formatProdengDate(date) : '';

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Article hero band */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(135deg, #6b2f04 0%, #92400e 50%, #78350f 100%)'
            : 'linear-gradient(135deg, #92400e 0%, #b45309 50%, #d97706 100%)',
          py: { xs: 5, md: 7 },
          px: 3,
        }}
      >
        <Container maxWidth="md">
          {/* Series breadcrumb */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
            <Button
              component={Link}
              to="/prodeng"
              startIcon={<ListIcon />}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                textTransform: 'none',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Product Engineering
            </Button>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>/</Typography>
            <Chip
              label={`Article ${articleNumber} of ${total}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
            {!published && (
              <Chip
                label="Preview"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.22)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Stack>

          {heroImage && (
            <Box
              component="img"
              src={heroImage}
              alt={title}
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 2,
                mb: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}
            />
          )}

          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
              lineHeight: 1.15,
              mb: 2,
              fontFamily: '"Georgia", "Times New Roman", serif',
            }}
          >
            {title}
          </Typography>

          {seoDescription && (
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.6,
                maxWidth: 600,
                mb: 3,
              }}
            >
              {seoDescription}
            </Typography>
          )}

          {tags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, mb: 3 }}>
              {tags.slice(0, 8).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: '0.9rem' }}>
              {author}
            </Typography>
            {formattedDate && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                  {formattedDate}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TimerIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                {readTime}
              </Typography>
            </Stack>
            {linkedinUrl && (
              <IconButton
                component="a"
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="Read on LinkedIn"
                onClick={() => analytics.outboundLinkClicked(linkedinUrl, 'prodeng_article')}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
              >
                <LinkedInIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Article content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {!published && (
          <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
            Preview on this site — not yet published on LinkedIn. This is the next piece in the series.
          </Alert>
        )}
        <MDXProvider>
          <Box
            className="prose"
            sx={{
              '& > *': { maxWidth: '100%' },
              '& .math, & .katex-display': { overflowX: 'auto', overflowY: 'hidden' },
              '& pre': { borderRadius: 2, my: 3 },
              '& img': { borderRadius: 2, boxShadow: 1 },
              '& .MuiTableContainer-root': {
                my: 3,
                width: '100% !important',
                maxWidth: '100% !important',
                ml: '0 !important',
                mr: '0 !important',
              },
              '& p': { mb: 2.5, lineHeight: 1.8 },
              '& h2': { mt: 5, mb: 2.5, fontFamily: '"Georgia", "Times New Roman", serif' },
              '& h3': { mt: 4, mb: 2, fontFamily: '"Georgia", "Times New Roman", serif' },
              '& h4': { mt: 3, mb: 1.5 },
              '& blockquote': {
                borderLeftColor: '#d97706',
              },
            }}
          >
            {children}
          </Box>
        </MDXProvider>

        <Divider sx={{ my: 5 }} />

        {/* Prev / Next navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 2 }}>
          {prev ? (
            <Button
              component={Link}
              to={prev.url as any}
              startIcon={<ArrowBackIcon />}
              onClick={() => analytics.articleNavigation('prev', slug, prev.url)}
              sx={{
                textTransform: 'none',
                textAlign: 'left',
                flex: 1,
                justifyContent: 'flex-start',
                py: 2,
                px: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: hexToRgba(accent, 0.25),
                color: accent,
                '&:hover': {
                  bgcolor: hexToRgba(accent, 0.07),
                  borderColor: accent,
                },
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Previous
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, color: accent }}>
                  {prev.title}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {next ? (
            <Button
              component={Link}
              to={next.url as any}
              endIcon={<ArrowForwardIcon />}
              onClick={() => analytics.articleNavigation('next', slug, next.url)}
              sx={{
                textTransform: 'none',
                textAlign: 'right',
                flex: 1,
                justifyContent: 'flex-end',
                py: 2,
                px: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: hexToRgba(accent, 0.25),
                color: accent,
                '&:hover': {
                  bgcolor: hexToRgba(accent, 0.07),
                  borderColor: accent,
                },
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Next
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25, color: accent }}>
                  {next.title}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
        </Box>

        {/* Back to journey */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            component={Link}
            to="/prodeng"
            variant="text"
            startIcon={<ListIcon />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back to all articles
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
